<script setup lang="ts">
import { ref, computed, watch, useSlots, onBeforeUnmount } from 'vue';
import { Offcanvas } from 'bootstrap';
import { useUxData } from '@/stores/ux-data';
import { teardownOffcanvas } from '@/composables/useOffcanvas';
import SearchField from '@/components/atoms/SearchField.vue';
import Pagination from '@/components/data/Pagination.vue';
import FilterBar from '@/components/atoms/element-filters/FilterBar.vue';
import FilterToggle from '@/components/atoms/element-filters/FilterToggle.vue';
import PageSizeControl from '@/components/atoms/PageSizeControl.vue';
import { useFilterBarWrap } from '@/composables/useFilterBarWrap';
import type { ColumnConfig, PaginationMetadata, PaginationLinks } from '@/types/browser';
import type { SettingElementInterface } from '@/types/items';

const props = withDefaults(
  defineProps<{
    dataItems: SettingElementInterface[];
    columns: ColumnConfig[];
    title?: string;
    selectedElement?: SettingElementInterface | null;
    loading?: boolean;
    metadata?: PaginationMetadata;
    links?: PaginationLinks;
    filtersActive?: boolean;
    showDetailsPanel?: boolean;
    showActionsColumn?: boolean;
    editingElementId?: string | null;
    searchPlaceholder?: string;
    // An element type's `empty` label, as the API sends it: the label heads the empty state and
    // the message sits beneath. The generic message stands in for a missing label only.
    emptyMessage?: { label?: string; message?: string };
    detailClass?: string;
    // Draws the session's page-size control (spec 2026-10-16) in a strip after the grid body, under
    // the pager's rule: only while the list has results. Off by default, so a grid whose list does
    // not follow the session's size draws no control.
    pageSizeControl?: boolean;
  }>(),
  {
    selectedElement: null,
    loading: false,
    metadata: () => ({ total: null, start: null, end: null }),
    links: () => ({ next: null, previous: null, current: null }),
    filtersActive: false,
    showDetailsPanel: false,
    showActionsColumn: false,
    editingElementId: null,
    pageSizeControl: false,
  },
);

const emit = defineEmits<{
  select: [element: SettingElementInterface];
  search: [event: any];
  next: [];
  previous: [];
  clear: [];
  rowDblClick: [element: SettingElementInterface];
}>();

const ux = useUxData();
const slots = useSlots();
const detailsRef = ref<HTMLElement | null>(null);
// A function, not a captured value: the #filters slot can be passed conditionally — a finder
// whose only filter is the Source control stops passing it once that control hides — and a
// boolean read once at setup would leave an empty .filter-bar behind. Mirrors hasCellSlot below.
const hasFilters = () => Boolean(slots.filters);
// One answer for the pager and the page-size control (spec 2026-10-16): both are drawn for a list
// whose metadata reports a total, and neither for an empty one or one that reports none.
const hasResults = computed(() => Boolean(props.metadata?.total));
const filterBar = ref<InstanceType<typeof FilterBar> | null>(null);
const gridSearch = ref<HTMLElement | null>(null);

// `order` has to be conditional: the bar belongs beside the search field while its chips fit, and
// after the pagination only once they have wrapped. See useFilterBarWrap for why CSS alone cannot
// decide this.
const { wrapped: filterBarWrapped } = useFilterBarWrap(filterBar, gridSearch);
const searchKey = ref(0);

const isEditing = (element: SettingElementInterface): boolean =>
  !element.uuid || element.uuid === props.editingElementId;

const isSelected = (element: SettingElementInterface): boolean =>
  props.selectedElement?.uuid === element.uuid;

const resolveValue = (element: SettingElementInterface, column: ColumnConfig): string => {
  if (column.formatter) return column.formatter(element);
  return column.field.split('.').reduce((obj: any, key) => obj?.[key], element)?.toString() ?? '';
};

const cellSlotName = (field: string) => `cell-${field}`;
const hasCellSlot = (field: string) => Boolean(slots[cellSlotName(field)]);

const showDetailsMobile = () => {
  if (detailsRef.value && window.innerWidth < 992) {
    Offcanvas.getOrCreateInstance(detailsRef.value).show();
  }
};

const onCloseDetailsMobile = () => {
  if (detailsRef.value) Offcanvas.getInstance(detailsRef.value)?.hide();
};

const onRowClick = (element: SettingElementInterface) => {
  emit('select', element);
};

// Open the mobile detail drawer reactively when a selection with content is made — NOT on
// the raw click. Opening on click popped a blank drawer when there was nothing to show
// (e.g. a world with no summary), and the offcanvas backdrop swallowed the second click of
// a double-click gesture. Tying it to selectedElement means the consumer decides whether a
// row has displayable detail (by setting/clearing selectedElement).
watch(
  () => props.selectedElement?.uuid,
  (uuid, previous) => {
    if (props.showDetailsPanel && uuid && uuid !== previous) showDetailsMobile();
  },
);
const onRowDblClick = (element: SettingElementInterface) => emit('rowDblClick', element);
const onSearch = (event: any) => emit('search', event);
// The pager is told when a fetch is in flight and waits for it — see Pagination.vue.
const onNext = () => emit('next');
const onPrevious = () => emit('previous');
// SearchField is uncontrolled; bumping its key remounts it empty so the visible
// text clears when the host resets the keyword slice.
const onClear = () => { emit('clear'); searchKey.value++; };
// The same for a host that has reset the keyword itself, without the user pressing Clear: a
// browser that starts fresh each time it opens.
const resetSearch = () => { searchKey.value++; };
defineExpose({ resetSearch });

// Template note (the spinner) — kept out of the template so it emits no comment vnode: while the
// list loads, the spinner stands in for the rows or the empty message, and for nothing else. The
// details panel stays mounted through it. Below lg that panel is an offcanvas, and it can be open
// when the list reloads; were the spinner to replace the whole body, the open panel's element
// would be deleted from under Bootstrap, which would go on holding the page's scroll lock.
//
// The detail drawer can still be open (mobile) when the list goes away. See teardownOffcanvas for
// why that takes more than a dispose(), and why it cannot wait for onUnmounted.
onBeforeUnmount(() => teardownOffcanvas(detailsRef.value));
</script>

<template>
  <div class="element-grid" :class="{ 'has-page-size': pageSizeControl }">
    <div class="grid-toolbar">
      <div ref="gridSearch" class="grid-search">
        <SearchField
          :key="searchKey"
          :placeholder="searchPlaceholder ?? (ux.labels as any)?.placeholders?.search ?? 'Search...'"
          @keyword-change="onSearch"
        />
      </div>
      <!-- A direct toolbar child, and ordered after the pagination at lg+ (see _element-grid.scss).
           Flexbox wraps on an item's base size, never because its own content wrapped, so a filter
           bar sitting beside the search field can only grow the toolbar and carry the pagination
           down with it. Ordered last it shares the row while there is room for it and drops to a
           full-width row of its own when there is not, leaving the pagination where it was. -->
      <FilterBar
        v-if="hasFilters()"
        ref="filterBar"
        class="filter-chips"
        :class="{ 'filter-bar-wrapped': filterBarWrapped }"
        :active="filtersActive"
        @clear="onClear"
      >
        <slot name="filters"></slot>
      </FilterBar>
      <FilterToggle v-if="hasFilters()" :active="filtersActive" @open="filterBar?.open()" />
      <div class="grid-actions">
        <slot name="actions"></slot>
      </div>
      <div v-if="hasResults" class="pagination">
        <Pagination
          :start="metadata.start"
          :end="metadata.end"
          :total="metadata.total"
          :disable-next="links.next === null"
          :disable-previous="links.previous === null"
          :loading="loading"
          @next="onNext"
          @previous="onPrevious"
        />
      </div>
    </div>

    <div class="grid-body">
      <div v-if="loading" class="grid-loading flex-grow-1">
        <span class="fa-light fa-fw fa-circle-notch fa-spin"></span>
      </div>
      <table v-else-if="dataItems.length > 0" class="element-table table table-hover">
        <thead>
          <tr>
            <th
              v-for="column in columns"
              :key="column.field"
              :class="['d-lg-table-cell d-none', ...(column.columnClass ?? [])]"
              scope="col"
            >
              {{ column.header }}
            </th>
            <th v-if="showActionsColumn" class="d-lg-table-cell d-none"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(element, rowIndex) in dataItems"
            :key="element.uuid ?? `pending-${rowIndex}`"
            role="button"
            :class="{ 'table-active': isSelected(element) }"
            @click="onRowClick(element)"
            @dblclick="onRowDblClick(element)"
          >
            <td
              v-for="(column, colIndex) in columns"
              :key="column.field"
              :class="['d-lg-table-cell', ...(column.columnClass ?? [])]"
              :data-header="colIndex > 0 ? column.header : undefined"
            >
              <slot
                v-if="hasCellSlot(column.field)"
                :name="cellSlotName(column.field)"
                :element="element"
                :isSelected="isSelected(element)"
                :isEditing="isEditing(element)"
              />
              <template v-else class="my-auto">{{ resolveValue(element, column) }}</template>
              <div v-if="$slots['row-actions']" class="d-lg-none">
                <slot
                    v-if="colIndex < 1"
                    name="row-actions"
                    class="d-lg-none my-auto"
                    :element="element"
                    :isEditing="isEditing(element)"
                />
              </div>
            </td>
            <td v-if="showActionsColumn" class="d-lg-table-cell actions text-lg-end text-center">
              <slot
                name="row-actions"
                :element="element"
                :isEditing="isEditing(element)"
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div v-else class="grid-empty flex-grow-1">
        <slot name="empty">
          <p class="h4 text-center">{{ emptyMessage?.label ?? (ux.labels as any)?.common?.emptyMessage ?? 'No results.' }}</p>
          <p v-if="emptyMessage?.message" class="text-center">{{ emptyMessage.message }}</p>
        </slot>
      </div>

      <div
        v-if="showDetailsPanel"
        ref="detailsRef"
        class="element-details card offcanvas-lg offcanvas-end"
        :class="[detailClass, { 'd-lg-none': !selectedElement }]"
        tabindex="-1"
      >
        <div class="offcanvas-header d-lg-none">
          <h6 class="offcanvas-title">{{ selectedElement?.name }}</h6>
          <button
            type="button"
            class="btn-close"
            :aria-label="(ux.labels as any)?.actions?.close ?? 'Close'"
            @click="onCloseDetailsMobile"
          ></button>
        </div>
        <div v-if="selectedElement" class="card-body details">
          <h6 class="card-title d-none d-lg-block">{{ selectedElement.name }}</h6>
          <slot name="details" :element="selectedElement"></slot>
        </div>
      </div>
    </div>

    <PageSizeControl v-if="pageSizeControl && hasResults" />
  </div>
</template>
